import { type AsyncResult, type VoidAsyncResult, asVoidAsyncResult } from "@/cache/AsyncResult";
import { WFS_TILE_ZOOM, getWfsTileWindow } from "@/cache/GeoJSONCache/tileUtils";
import { networkCaches } from "@/cache/GlobalCache";
import { ChangeDetector } from "@/layers/ChangeDetector";
import type { ScenePublicApi } from "@/layers/Compositor";
import type { CheckedProps, LayerBase, PropsChecker } from "@/layers/LayerBase";
import { generateWfsCacheId } from "@/layers/geojson/LayerUtils";
import { MapboxGeoJSONLayer } from "@/layers/geojson/MapboxGeoJSONLayer";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { renderPopup } from "@/layers/utility/popup";
import { SENTRY_TRANSACTION_NAMES } from "@/sentry/TransactionConst";
import { captureTransaction } from "@/sentry/helpers";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import { t } from "@lingui/core/macro";
import type { CoordinateSystem, GeoJSONFeatureCollection, WfsRequest } from "@mm/api.meteomatics.com";
import Logger from "@mm/logging";
import type { GuiTimeZone, StationLayer } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime } from "luxon";
import type { DataDrivenPropertyValueSpecification, Expression } from "mapbox-gl";
import mapboxgl from "mapbox-gl";
import {
  INDONESIA_BBOX,
  STATION_LABEL_OVERLAP_ZOOM,
  convertWFSParamUnitToParamUnit,
  createGeoJSONFormatter,
  getParamDisplayName,
  getStationWeatherIconSize,
  isWeatherSymbolParamUnit,
} from "./StationLayerImplUtils";
const logger = Logger.fromFilename(__filename);

export class StationLayerImpl extends MapboxGeoJSONLayer<StationLayer> {
  private popup = new mapboxgl.Popup({ maxWidth: "420px", className: "mapbox-popover mapbox-popover-nwa" });
  private lastOverlapAllowed: boolean | null = null;
  private readonly displayTimeChanged = new ChangeDetector<number>();
  /**
   * Mapbox GeoJSON Feature object that a user clicked on.
   */
  private selectedFeature: mapboxgl.MapboxGeoJSONFeature | null = null;
  constructor(id: number, props: StationLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);
    this.updateMapboxLayoutProperties({
      visibility: this.props.show ? "visible" : "none",
      "text-field": this.getTextFieldExpression(props.parameter_unit),
      "text-keep-upright": false,
      "text-size": this.props.text_size,
      "text-allow-overlap": false,
      "text-ignore-placement": false,
    });
    this.updateIconLayout(props.parameter_unit, this.props.text_size);
    this.updateMapboxPaintProperties({
      "text-color": this.props.text_color ?? undefined,
      "text-opacity": this.props.opacity,
      // "text-halo-color": `rgba(256, 256, 256,  ${this.props.opacity})`,
      // "text-halo-width": 1,
    });
    this.updateOverlapForZoom();
    this.setEventListeners();
  }

  private updateOverlapForZoom() {
    const mapBoxMap = this.scene.getMapboxMap();
    if (!mapBoxMap.getLayer(this.mapbox_id)) {
      return;
    }
    // Custom rule: overlap is enabled only when the map center is inside Indonesia
    // and the zoom level is above the configured threshold.
    const center = mapBoxMap.getCenter();
    const inIndonesia =
      center.lng >= INDONESIA_BBOX.west &&
      center.lng <= INDONESIA_BBOX.east &&
      center.lat >= INDONESIA_BBOX.south &&
      center.lat <= INDONESIA_BBOX.north;
    const allowOverlap = mapBoxMap.getZoom() > STATION_LABEL_OVERLAP_ZOOM && inIndonesia;
    if (this.lastOverlapAllowed === allowOverlap) {
      return;
    }
    this.lastOverlapAllowed = allowOverlap;
    this.updateMapboxLayoutProperties({
      "text-allow-overlap": allowOverlap,
      "text-ignore-placement": allowOverlap,
      "icon-allow-overlap": allowOverlap,
      "icon-ignore-placement": allowOverlap,
    });
  }

  setEventListeners() {
    const mapBoxMap = this.scene.getMapboxMap();

    // Add a popup window on click.
    mapBoxMap.on("click", this.mapbox_id, (e) => {
      // TODO-StationFeature:
      // Replace the popup here and migrate it to PlotWindow. Instead, we can dispatch an action here
      // to Redux store to store the clicked station. Then, PlotWindow can react to it, which, in turn, fetches the station data.
      if (!e.features) {
        return;
      }
      const stationFeature = e.features[0];
      this.selectedFeature = stationFeature;

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      if (stationFeature.geometry.type === "Point") {
        const stationProperties = stationFeature.properties ?? {};
        const popupHTML = renderPopup({
          title: String(stationProperties.name ?? t`Station`),
          rows: [
            ["WMO_ID", stationProperties.WMO_ID || t`No Information`],
            ["Alternative ID", stationProperties.alternative_ID || ""],
            ["Elevation", stationProperties.elevation || t`No Information`],
            [
              getParamDisplayName(this.props.parameter_unit, this.props.model),
              stationProperties.decimalValue || t`No Information`,
            ],
          ],
        });
        const coordinates = stationFeature.geometry.coordinates;
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        this.popup.setLngLat(new mapboxgl.LngLat(coordinates[0], coordinates[1])).setHTML(popupHTML).addTo(mapBoxMap);
        return;
      }
      logger.error(
        "The clicked station feature did not contain 'Point' type geometry, instead contained:",
        stationFeature.geometry.type,
        stationFeature,
      );
    });

    mapBoxMap.on("zoomend", () => {
      this.updateOverlapForZoom();
    });
    mapBoxMap.on("moveend", () => {
      this.updateOverlapForZoom();
    });
  }

  humanReadableId(): string {
    return safeFmt`metx.stationlayer#${this.uid}#${this.props.model}.${this.props.parameter_unit}`;
  }

  checker(): PropsChecker<StationLayer, LayerBase<StationLayer>> {
    type CheckerProps = CheckedProps<StationLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (changed) {
        this.setLayerProps({ [property]: curr[property] });
        switch (property) {
          case "model":
            this.beforeRender();
            break;
          case "show":
            this.updateMapboxLayoutProperties({ visibility: curr.show ? "visible" : "none" });
            break;
          case "opacity":
            this.updateMapboxPaintProperties({
              "text-opacity": curr[property],
            });
            break;
          case "calibrated":
            break;
          case "parameter_unit":
            this.updateMapboxLayoutProperties({
              "text-field": this.getTextFieldExpression(curr[property]),
            });
            this.updateIconLayout(curr[property], this.props.text_size);
            this.beforeRender();
            break;
          case "ens_select":
            this.beforeRender();
            break;
          case "text_size":
            this.updateMapboxLayoutProperties({
              "text-size": curr[property],
            });
            this.updateIconLayout(this.props.parameter_unit, curr[property]);
            break;
          case "text_color":
            this.updateMapboxPaintProperties({
              "text-color": curr[property],
            });
            break;
          case "custom_options":
            break;
          default: {
            const _exhaustive: never = property;
            return _exhaustive;
          }
        }
      }
      return changed;
    };
    return {
      model: checkLayerProp("model"),
      opacity: checkLayerProp("opacity"),
      calibrated: checkLayerProp("calibrated"),
      parameter_unit: checkLayerProp("parameter_unit"),
      show: checkLayerProp("show"),
      text_size: checkLayerProp("text_size"),
      text_color: checkLayerProp("text_color"),
      ens_select: checkLayerProp("ens_select"),
      custom_options: checkLayerProp("custom_options"),
    };
  }

  private getTextFieldExpression(parameterUnit: string): DataDrivenPropertyValueSpecification<string> {
    if (isWeatherSymbolParamUnit(parameterUnit)) {
      return "";
    }
    const expr: Expression = [
      "case",
      // Check if the value exists, if true
      ["has", convertWFSParamUnitToParamUnit(parameterUnit)],
      ["get", convertWFSParamUnitToParamUnit(parameterUnit)],
      // If the value doesn't exist, render an empty string.
      "",
    ];
    return expr;
  }

  private getIconImageExpression(): DataDrivenPropertyValueSpecification<string> {
    const expr: Expression = ["get", "weather-icon"];
    return expr;
  }

  private getIconSize(textSize?: number) {
    return getStationWeatherIconSize(textSize ?? this.props.text_size);
  }

  private updateIconLayout(parameterUnit: string, textSize?: number) {
    if (!isWeatherSymbolParamUnit(parameterUnit)) {
      // Unset icon layout properties to avoid sprite resolution for non-weather parameters.
      this.updateMapboxLayoutProperties({
        "icon-image": undefined as unknown as DataDrivenPropertyValueSpecification<string>,
        "icon-size": undefined as unknown as number,
        "icon-allow-overlap": undefined as unknown as boolean,
        "icon-ignore-placement": undefined as unknown as boolean,
      });
      return;
    }
    this.updateMapboxLayoutProperties({
      "icon-image": this.getIconImageExpression(),
      "icon-size": this.getIconSize(textSize),
      "icon-allow-overlap": false,
      "icon-ignore-placement": false,
    });
  }

  createRequest(dateTimeWithOffset: DateTime): WfsRequest<CoordinateSystem.WGS84> {
    // Currently we fetch the station data for the entire world because GeoJSON cache cache based on the bounding box.
    // This means, if we move the viewport, we'd fetch the data that already exists.
    // To speed up the loading for smaller grid, we should probably split up the grids and fetch only the data we need.
    const { area } = this.computeBoundingBox(0);
    // Currently, the station models at the WFS endpoint are formatted differently than the ones on the regular endpoint.
    // e.g) The regular endpoint uses "mix-obs" but the WFS interface TYPENAME uses "stations_mix-obs"
    // So we add the stations prefix for every station model.
    return {
      requestType: "GetFeature",
      area: area,
      parameters: [this.props.parameter_unit],
      model: `stations_${this.props.model}`,
      boundingBoxLimit: getModelBoundingBox(this.props.model),
      datetime: dateTimeWithOffset,
    };

    // return different request for winterdienst stations?
  }
  beforeRender(): void {
    if (
      this.displayTimeChanged.changed(
        roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName).toMillis(),
      )
    ) {
      this.popup.remove();
    }

    if (this.scene.isAnimation()) {
      this.updateTrigger();
    } else {
      this.debouncedUpdateTrigger();
    }
  }
  updateTrigger(): void {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);

    const result = networkCaches.geojson_cache.retrieveWfs(request, createGeoJSONFormatter(this.props.parameter_unit), {
      owner: this.getGeoJsonRequestOwner(),
    });
    this.cancelSupersededGeoJsonRequests(result);
    this.updateSource(result, dateTimeWithOffset, undefined, {
      keepLastDataWhilePending: this.scene.isAnimation(),
    });
  }
  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(timeFrame),
      this.props.kind as LayerKindName,
    );
    const request = this.createRequest(dateTimeWithOffset);
    const tags = {
      layerKind: `${this.props.kind ? this.props.kind : "undefined"}`,
      parameter: `${this.props.parameter_unit}`,
      requestedDate: `${dateTimeWithOffset}`,
    };

    const result = networkCaches.geojson_cache.retrieveWfs(request, createGeoJSONFormatter(this.props.parameter_unit));

    if (!this.scene.isAnimation()) {
      return asVoidAsyncResult(
        result,
        captureTransaction(result.asynchronous, SENTRY_TRANSACTION_NAMES.STATION_LAYER_LOADING_SPEED, tags),
      );
    }

    return asVoidAsyncResult(result);
  }

  getActiveWeatherParametersAsString() {
    return [
      {
        model: this.props.model,
        parameter: this.props.parameter_unit,
      },
    ];
  }

  peekLayerCache(request: WfsRequest<CoordinateSystem.WGS84>): AsyncResult<GeoJSONFeatureCollection> {
    const tileWindow = getWfsTileWindow(request, WFS_TILE_ZOOM);
    if (!tileWindow) {
      const cacheId = generateWfsCacheId(request);
      return networkCaches.geojson_cache.peekCache(cacheId);
    }
    return networkCaches.geojson_cache.peekCache(tileWindow.aggregateCacheId);
  }
}
